import { PDFDocument } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";

type PdfDocument = Awaited<ReturnType<typeof PDFDocument.create>>;
type PdfFont = Awaited<ReturnType<PdfDocument["embedFont"]>>;

import type {
  ProjectBriefBullet,
  ProjectBriefContent,
  ProjectBriefSection,
  ProjectBriefStatus,
} from "../types/projectBrief";

interface ExportParams {
  brief: ProjectBriefContent;
  projectId?: string;
  status?: ProjectBriefStatus;
}

function normalizeText(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();

  return trimmed.length ? trimmed : null;
}

function isProjectBriefBullet(value: unknown): value is ProjectBriefBullet {
  return typeof value === "object" && value !== null && "text" in value;
}

function normalizeList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (typeof entry === "string") {
        return normalizeText(entry);
      }

      if (isProjectBriefBullet(entry)) {
        return normalizeText(entry.text);
      }

      return null;
    })
    .filter((entry): entry is string => Boolean(entry));
}

function renderField(value: string | string[] | ProjectBriefBullet[] | undefined | null): string[] | null {
  if (Array.isArray(value)) {
    const items = normalizeList(value);
    return items.length ? items.map((item) => `- ${item}`) : null;
  }

  const text = normalizeText(value);

  return text ? [text] : null;
}

function appendSection(lines: string[], heading: string, value: string | string[] | undefined | null): void {
  const body = renderField(value);

  if (!body) return;

  lines.push("", `## ${heading}`, "", ...body);
}

function appendDetailedSections(lines: string[], sections: ProjectBriefSection[]): void {
  const validSections = sections.filter((section) => normalizeText(section?.title));

  if (!validSections.length) return;

  lines.push("", "## Sections détaillées");

  validSections.forEach((section) => {
    const title = normalizeText(section.title) ?? "Sans titre";
    lines.push("", `### ${title}`);

    const summary = normalizeText(section.summary);
    const details = normalizeText(section.details);
    const bullets = renderField(section.bullets ?? undefined);

    if (summary) {
      lines.push("", summary);
    }

    if (details) {
      lines.push("", details);
    }

    if (bullets) {
      lines.push("", ...bullets);
    }
  });
}

export function projectBriefToMarkdown(brief: ProjectBriefContent, status?: ProjectBriefStatus): string {
  const record = (brief ?? {}) as unknown as Record<string, unknown>;
  const lines: string[] = [];

  if (status === "DRAFT") {
    lines.push("> **STATUT : BROUILLON**", "", "---");
  }

  const title = normalizeText(record.title) ?? "Brief projet";
  lines.push(`# ${title}`);

  appendSection(lines, "Résumé", record.summary as string);
  appendSection(lines, "Contexte / Vue d’ensemble", record.overview as string);
  appendSection(lines, "Objectifs", record.goals as string[]);
  appendSection(lines, "Périmètre (Scope)", record.scope as string[]);
  appendSection(lines, "Hors périmètre (Non-goals)", record.nonGoals as string[]);
  appendSection(lines, "Contraintes", record.constraints as string[]);
  appendSection(lines, "Risques", record.risks as string[]);
  appendSection(lines, "Recommandations", (record as Record<string, string[] | string | null | undefined>).recommendations);
  appendSection(lines, "Prochaines étapes", record.nextSteps as string[]);

  if (Array.isArray(record.sections)) {
    appendDetailedSections(lines, record.sections as ProjectBriefSection[]);
  }

  const markdownNotes = normalizeText((record as Record<string, unknown>).markdown);

  if (markdownNotes) {
    lines.push("", markdownNotes);
  }

  const markdownDocument = lines.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";

  return markdownDocument;
}

function ensureClientSide(): void {
  if (typeof window === "undefined") {
    throw new Error("L’export doit être lancé côté client.");
  }
}

function downloadBlob(blob: Blob, filename: string): void {
  ensureClientSide();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function exportProjectBriefAsMarkdown({ brief, projectId, status }: ExportParams): Promise<{
  blob: Blob;
  filename: string;
}> {
  const markdown = projectBriefToMarkdown(brief, status);
  const blob = new Blob([markdown], { type: "text/markdown; charset=utf-8" });
  const filename = `brief-projet-${projectId ?? "document"}.md`;

  return { blob, filename };
}

interface PdfLineOptions {
  fontSize: number;
  indent?: number;
}

type TextMeasurer = (text: string, fontSize: number) => number;

async function loadEmbeddedFont(pdfDoc: PdfDocument): Promise<PdfFont> {
  const response = await fetch("/fonts/DejaVuSans.ttf");

  if (!response.ok) {
    throw new Error(`Impossible de charger la police pour l'export PDF (${response.status}).`);
  }

  const fontBytes = await response.arrayBuffer();

  return pdfDoc.embedFont(fontBytes);
}

function wrapText(text: string, measureText: TextMeasurer, fontSize: number, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    const tentative = current ? `${current} ${word}` : word;
    const width = measureText(tentative, fontSize);
    if (width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = tentative;
    }
  });

  if (current) {
    lines.push(current);
  }

  return lines.length ? lines : [""];
}

function markdownLineToPdfLine(line: string): { text: string; options: PdfLineOptions } {
  if (/^#\s+/.test(line)) {
    return { text: line.replace(/^#\s+/, "").trim(), options: { fontSize: 18 } };
  }

  if (/^##\s+/.test(line)) {
    return { text: line.replace(/^##\s+/, "").trim(), options: { fontSize: 16 } };
  }

  if (/^###\s+/.test(line)) {
    return { text: line.replace(/^###\s+/, "").trim(), options: { fontSize: 14 } };
  }

  if (/^[-*]\s+/.test(line)) {
    return { text: `• ${line.replace(/^[-*]\s+/, "").trim()}`, options: { fontSize: 12, indent: 12 } };
  }

  return { text: line.trim(), options: { fontSize: 12 } };
}

export async function exportProjectBriefAsPdf({ brief, projectId, status }: ExportParams): Promise<{
  blob: Blob;
  filename: string;
}> {
  ensureClientSide();

  const pdfDoc = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit);
  const font = await loadEmbeddedFont(pdfDoc);

  const margin = 50;
  let currentPage = pdfDoc.addPage();
  let cursorY = currentPage.getSize().height - margin;

  const markdown = projectBriefToMarkdown(brief, status);
  const lines = markdown.split(/\r?\n/);

  const addNewPage = () => {
    const newPage = pdfDoc.addPage();
    cursorY = newPage.getSize().height - margin;
    return newPage;
  };

  lines.forEach((rawLine) => {
    const { text, options } = markdownLineToPdfLine(rawLine);
    const lineHeight = Math.max(options.fontSize + 4, 14);
    const maxWidth = currentPage.getSize().width - margin * 2 - (options.indent ?? 0);

    if (!text) {
      cursorY -= lineHeight / 2;
      return;
    }

    const wrapped = wrapText(text, (content, size) => font.widthOfTextAtSize(content, size), options.fontSize, maxWidth);

    wrapped.forEach((wrappedLine) => {
      if (cursorY - lineHeight < margin) {
        currentPage = addNewPage();
      }

      currentPage.drawText(wrappedLine, {
        x: margin + (options.indent ?? 0),
        y: cursorY,
        size: options.fontSize,
        font,
      });

      cursorY -= lineHeight;
    });
  });

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const filename = `brief-projet-${projectId ?? "document"}.pdf`;

  return { blob, filename };
}

export function downloadProjectBriefMarkdown(params: ExportParams): Promise<void> {
  return exportProjectBriefAsMarkdown(params).then(({ blob, filename }) => downloadBlob(blob, filename));
}

export function downloadProjectBriefPdf(params: ExportParams): Promise<void> {
  return exportProjectBriefAsPdf(params).then(({ blob, filename }) => downloadBlob(blob, filename));
}
